import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, Star, MapPin, Clock, Users, Check, Palmtree, CreditCard } from "lucide-react";
import { isProUser } from "../lib/patron";

function ExperienceDetailPage() {
  const { experienceId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [experience, setExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [participants, setParticipants] = useState(1);
  const [bookingLoading, setBookingLoading] = useState(false);

  const fetchExperience = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/experiences/${experienceId}`);
      setExperience(response.data);
    } catch (error) {
      toast.error("Failed to load experience");
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    fetchExperience();
  }, [fetchExperience]);

  const handleBooking = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!bookingDate || !bookingTime) {
      toast.error("Please select date and time");
      return;
    }
    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        experience_id: experienceId,
        booking_date: bookingDate,
        booking_time: bookingTime,
        participants: participants
      }, { withCredentials: true });

      if (response.data?.type === "inquiry") {
        toast.success(response.data.message || "Inquiry sent to the business.");
        navigate("/bookings");
        return;
      }

      const bookingId =
        response.data?.booking_id ||
        response.data?.booking?.booking_id;
      if (!bookingId) {
        toast.error("Booking created but payment could not start.");
        return;
      }

      const paymentResponse = await axios.post(`${API}/payments/checkout`, {
        payment_type: "booking",
        item_id: bookingId,
        origin_url: window.location.origin
      }, { withCredentials: true });

      if (paymentResponse.data?.url) {
        window.location.href = paymentResponse.data.url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail?.message || detail?.upsell?.headline || "Booking failed";
      toast.error(message);
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-80 bg-gray-200 animate-pulse" />
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="h-10 w-3/4 bg-gray-200 animate-pulse rounded mb-4" />
        </div>
      </div>
    );
  }

  if (!experience) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Palmtree className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Experience not found</h2>
          <Link to="/experiences"><Button>Back to Experiences</Button></Link>
        </div>
      </div>
    );
  }

  const imageUrl = experience.images && experience.images.length > 0 ? experience.images[0] : "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200";
  const rating = experience.rating ? experience.rating.toFixed(1) : "4.5";
  const totalPrice = experience.price * participants;
  const isPremium = isProUser(user);
  const discount = isPremium ? totalPrice * 0.1 : 0;
  const finalPrice = totalPrice - discount;
  const availableTimes = ["9:00 AM", "10:00 AM", "2:00 PM", "3:00 PM"];
  const includedItems = experience.includes || [];
  const requirements = experience.requirements || [];

  const formatDuration = (mins) => {
    const hours = Math.floor(mins / 60);
    const m = mins % 60;
    if (hours > 0 && m > 0) return `${hours}h ${m}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative h-80 md:h-96">
        <img src={imageUrl} alt={experience.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-6 left-6">
          <Link to="/experiences">
            <Button variant="outline" className="bg-white/80 rounded-full" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5 mr-1" /> Back
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Badge variant="outline" className="mb-3 capitalize">{experience.category}</Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">{experience.name}</h1>
            <div className="flex items-center gap-4 text-gray-600 mb-8">
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="font-bold text-black">{rating}</span>
              </span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> <span className="capitalize">{experience.country}</span></span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-100 rounded-2xl p-4 text-center">
                <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-gray-600">Duration</p>
                <p className="font-bold">{formatDuration(experience.duration_minutes)}</p>
              </div>
              <div className="bg-gray-100 rounded-2xl p-4 text-center">
                <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-gray-600">Group Size</p>
                <p className="font-bold">Up to {experience.max_participants}</p>
              </div>
              <div className="bg-gray-100 rounded-2xl p-4 text-center">
                <CreditCard className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-gray-600">Price</p>
                <p className="font-bold">${experience.price}/person</p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4">About This Experience</h2>
              <p className="text-gray-600 leading-relaxed">{experience.description}</p>
            </div>

            {includedItems.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-4">What's Included</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {includedItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {requirements.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-4">Requirements</h2>
                <ul className="space-y-2">
                  {requirements.map((req, i) => (
                    <li key={i} className="text-gray-600 flex items-start gap-2">
                      <span className="text-primary">•</span>{req}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-3xl shadow-lg p-6">
              <div className="text-center mb-6">
                <p className="text-3xl font-bold text-primary">${experience.price}<span className="text-base font-normal text-gray-500"> /person</span></p>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">Select Date</label>
                <Input type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full" data-testid="date-input" />
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">Select Time</label>
                <Select value={bookingTime} onValueChange={setBookingTime}>
                  <SelectTrigger data-testid="time-select"><SelectValue placeholder="Choose a time" /></SelectTrigger>
                  <SelectContent>
                    {availableTimes.map((time) => <SelectItem key={time} value={time}>{time}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <label className="text-sm font-medium mb-2 block">Participants</label>
                <Select value={participants.toString()} onValueChange={(v) => setParticipants(parseInt(v))}>
                  <SelectTrigger data-testid="participants-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: experience.max_participants }, (_, i) => i + 1).map((num) => (
                      <SelectItem key={num} value={num.toString()}>{num} {num === 1 ? "person" : "people"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4 mb-6 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">${experience.price} × {participants}</span>
                  <span>${totalPrice.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Premium discount (10%)</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>${finalPrice.toFixed(2)}</span>
                </div>
              </div>

              <Button className="w-full bg-primary text-white rounded-full py-6" onClick={handleBooking} disabled={bookingLoading || !bookingDate || !bookingTime} data-testid="book-now-btn">
                {bookingLoading ? "Processing..." : "Book Now"}
              </Button>

              {!user && <p className="text-center text-sm text-gray-500 mt-4"><Link to="/auth" className="text-primary hover:underline">Sign in</Link> to book</p>}

              {user && !isPremium && (
                <div className="mt-4 bg-yellow-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">Save 10% with Premium!</p>
                  <Link to="/premium"><Button variant="outline" size="sm" data-testid="upgrade-premium-btn">Upgrade Now</Button></Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExperienceDetailPage;
